import { useRef, useState, useEffect } from 'react';
import { Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { uboTheme } from '@/theme/uboTheme';
import { toast } from '@/lib/toast';
import api from '@/lib/api';
import { useClientLogoWhiteFilterOnDark } from '@/store/quoteClientLogoContext';

/** UBO: agency blauw op lichte pagina, geen filter op donker. UBX: origineel op licht, wit op donker + groter logo. */
export type PageHeaderBrand = 'ubo' | 'ubx';

interface PageHeaderProps {
  clientLogoUrl?: string;
  agencyLogoUrl?: string;
  variant?: 'light' | 'dark';
  /** Standaard UBO. Alleen bij UBX-template blokken `ubx` zetten. */
  brand?: PageHeaderBrand;
  isEditable?: boolean;
  onClientLogoChange?: (url: string) => void;
}

const FILTER_UBO_AGENCY_ON_LIGHT =
  'brightness(0) saturate(100%) invert(11%) sepia(98%) saturate(7404%) hue-rotate(243deg) brightness(83%) contrast(145%)';
const FILTER_WHITE_ON_DARK = 'brightness(0) invert(1)';

export default function PageHeader({
  clientLogoUrl,
  agencyLogoUrl,
  variant = 'dark',
  brand = 'ubo',
  isEditable = false,
  onClientLogoChange,
}: PageHeaderProps) {
  const clientLogoWhiteFilterOnDark = useClientLogoWhiteFilterOnDark();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [clientLogoError, setClientLogoError] = useState(false);
  const [agencyLogoError, setAgencyLogoError] = useState(false);

  useEffect(() => {
    setClientLogoError(false);
    setAgencyLogoError(false);
  }, [clientLogoUrl, agencyLogoUrl]);

  const logoColor = variant === 'light' ? 'primary' : 'white';
  const isUbx = brand === 'ubx';

  /** Links: klant. Rechts: agency — alleen UBX-agency (110px). */
  const clientLogoHeight = isUbx ? 56 : 50;
  const clientLogoMaxWidth = isUbx ? 240 : 180;
  const agencyLogoHeight = isUbx ? 110 : 50;
  const agencyLogoMaxWidth = isUbx ? 340 : 180;
  const minH = Math.max(clientLogoHeight, agencyLogoHeight);

  const clientFilter =
    variant === 'dark' && clientLogoWhiteFilterOnDark ? FILTER_WHITE_ON_DARK : 'none';

  let agencyFilter: string;
  if (isUbx) {
    agencyFilter = variant === 'dark' ? FILTER_WHITE_ON_DARK : 'none';
  } else {
    agencyFilter = variant === 'light' ? FILTER_UBO_AGENCY_ON_LIGHT : 'none';
  }

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const result = await api.uploadImage(file);
      const fullUrl = api.resolveUploadUrl(result.url);
      onClientLogoChange?.(fullUrl);
    } catch (err) {
      console.error('Upload failed:', err);
      toast.error('Logo uploaden mislukt', { error: err });
    }

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="flex justify-between items-center z-10 relative" style={{ minHeight: minH }}>
      {/* LEFT: Client logo */}
      <div className="flex items-center gap-2" style={{ minHeight: minH }}>
        {clientLogoUrl && !clientLogoError ? (
          <img
            src={clientLogoUrl}
            alt="Klant logo"
            className="page-chrome-image"
            style={{
              height: clientLogoHeight,
              width: 'auto',
              maxWidth: clientLogoMaxWidth,
              objectFit: 'contain',
              filter: clientFilter,
            }}
            onError={() => setClientLogoError(true)}
          />
        ) : isEditable ? (
          <div
            data-html2canvas-ignore
            className="px-4 flex items-center border-2 border-dashed rounded cursor-pointer hover:opacity-80"
            style={{
              height: minH,
              borderColor: variant === 'light' ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.3)',
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload
              className="mr-1 h-[18px] w-[18px]"
              style={{ color: variant === 'light' ? 'hsl(var(--muted-foreground))' : 'rgba(255,255,255,0.6)' }}
            />
            <span
              className="text-xs"
              style={{ color: variant === 'light' ? 'hsl(var(--muted-foreground))' : 'rgba(255,255,255,0.6)' }}
            >
              Upload klant logo
            </span>
          </div>
        ) : null}

        {isEditable && clientLogoUrl && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => fileInputRef.current?.click()}
                style={{ color: variant === 'light' ? 'hsl(var(--muted-foreground))' : 'rgba(255,255,255,0.6)' }}
              >
                <Upload className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Ander logo uploaden</TooltipContent>
          </Tooltip>
        )}

        {isEditable && (
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileUpload}
          />
        )}
      </div>

      {/* RIGHT: Agency logo */}
      <div className="flex items-center" style={{ flexShrink: 0 }}>
        {(agencyLogoUrl && !agencyLogoError) ? (
          <img
            src={agencyLogoUrl}
            alt="UBO"
            className="page-chrome-image"
            style={{
              height: agencyLogoHeight,
              width: 'auto',
              maxWidth: agencyLogoMaxWidth,
              objectFit: 'contain',
              filter: agencyFilter,
            }}
            onError={() => setAgencyLogoError(true)}
          />
        ) : (
          <span
            className="text-[28px] font-black uppercase tracking-wider"
            style={{
              color: logoColor === 'primary' ? uboTheme.colors.primary : 'white',
              fontFamily: uboTheme.fonts.primaryFallback,
            }}
          >
            UBO
          </span>
        )}
      </div>
    </div>
  );
}
